import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { onSnapshot, query, where } from "firebase/firestore";
import { auth } from "../../firebase";
import Window from "../../components/Window";
import Icon from "../../components/Icon";
import ContentWarning from "../../components/ContentWarning";
import { PostComposer } from "../../components/Postcards";
import { sanitizeHtml } from "../../sanitize";
import { getUsername } from "../../penpal";
import { Piece, deletePiece, deliverPiece, piecesCol, postDispatch, returnPiece, useIsEditor } from "../../post";
import "../../styles.css";

// The sorting room: where editors read submissions and deliver them to the
// PenPal Post or return them with a note. Also where dispatches are written.
export default function SortingRoom() {
  const [user] = useAuthState(auth);
  const editor = useIsEditor(user?.uid);
  const [pieces, setPieces] = useState<Piece[] | null>(null);

  useEffect(() => {
    if (!editor) return;
    return onSnapshot(
      query(piecesCol, where("status", "==", "sorting")),
      (snap) =>
        setPieces(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as Omit<Piece, "id">) }))
            .filter((p) => !p.removed)
            .sort((a, b) => (a.submittedAt?.toMillis() ?? 0) - (b.submittedAt?.toMillis() ?? 0))
        ),
      () => setPieces([])
    );
  }, [editor]);

  if (!user) return null;
  if (!editor) {
    return (
      <div className="window">
        <p className="empty">
          The sorting room is for the PenPal Post's editors. <Link to="/post">Back to the PenPal Post</Link>
        </p>
      </div>
    );
  }

  return (
    <>
      <Window title="sorting_room.exe" tone="tan">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="window-heading flex items-center gap-2">
              <Icon name="inbox" size={26} tone="pink" pop /> The sorting room
            </h1>
            <p className="window-sub !mb-0">
              Read what's come in, then deliver it to the PenPal Post or return it
              to the writer with a kind note. Oldest first.
            </p>
          </div>
          <Link to="/post" className="btn btn-plain btn-sm">
            <Icon name="back" size={16} /> The PenPal Post
          </Link>
        </div>
      </Window>

      {pieces === null ? (
        <p className="empty">Opening the mailbag...</p>
      ) : pieces.length === 0 ? (
        <div className="window">
          <p className="empty">The mailbag is empty. Nothing waiting to be sorted. ✿</p>
        </div>
      ) : (
        pieces.map((p) => <SortItem key={p.id} p={p} />)
      )}

      <Window title="write_a_dispatch.exe" tone="blue">
        <details>
          <summary className="btn btn-blue cursor-pointer list-none">
            <Icon name="edit" size={16} /> Write a dispatch from the Post Office
          </summary>
          <p className="mt-3 text-sm text-ink-soft">
            News, updates, and site history. Dispatches are delivered right away and
            signed "the Post Office".
          </p>
          <PostComposer
            titleLabel="Dispatch title"
            titlePlaceholder="e.g. Welcome to the PenPal Post!"
            bodyPlaceholder="What's new at the Post Office?"
            categoryOptions={[]}
            anonymousHint="Dispatches are always signed by the Post Office, so this box is ignored."
            submitLabel="Deliver the dispatch"
            onPost={async (d) => {
              await postDispatch(user.uid, await getUsername(user.uid), {
                subject: d.subject,
                message: d.message,
                color: d.color,
                cw: d.cw,
              });
              alert("Delivered! ✿");
            }}
          />
        </details>
      </Window>
    </>
  );
}

function SortItem({ p }: { p: Piece }) {
  const [note, setNote] = useState("");
  const [returning, setReturning] = useState(false);
  const [busy, setBusy] = useState(false);
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      console.error(err);
      alert("Sorry, that didn't work. Please try again.");
      setBusy(false);
    }
  };

  return (
    <Window title={`${p.category || "submission"}.txt`} tone="mint">
      <p className="mb-1 text-sm text-ink-soft">
        {p.category} · from {p.anonymous ? "anonymous" : <Link to={`/profile/${p.uid}`}>@{p.username}</Link>}
        {p.submittedAt && ` · sent ${p.submittedAt.toDate().toLocaleDateString()}`}
      </p>
      <h2 className="window-heading mb-3">{p.title}</h2>
      <ContentWarning cw={p.cw}>
        <div
          className="lined-paper max-h-[50vh] overflow-y-auto"
          style={p.color ? { background: p.color } : undefined}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(p.body) }}
        />
      </ContentWarning>

      <div className="divider my-4" />
      {!returning ? (
        <div className="btn-row">
          <button type="button" className="btn" disabled={busy} onClick={() => act(() => deliverPiece(p.id))}>
            <Icon name="send" size={16} /> Deliver it
          </button>
          <button type="button" className="btn btn-plain" disabled={busy} onClick={() => setReturning(true)}>
            Return to sender...
          </button>
          <button
            type="button"
            className="btn btn-plain btn-sm"
            disabled={busy}
            onClick={() =>
              window.confirm("Delete this submission entirely? (For spam or harmful content.)") &&
              act(() => deletePiece(p.id))
            }
          >
            Delete
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <label className="field">
            <span className="field-label">A kind note to the writer (they'll see this)</span>
            <textarea
              className="input"
              value={note}
              maxLength={1000}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. We loved this! Could you add a content warning for the hospital part and send it again?"
            />
          </label>
          <div className="btn-row">
            <button type="button" className="btn" disabled={busy} onClick={() => act(() => returnPiece(p.id, note))}>
              Return it
            </button>
            <button type="button" className="btn btn-plain" onClick={() => setReturning(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </Window>
  );
}
